import React, { useCallback, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import BandwidthActivityByWeekChart from './BandwidthActivityByWeekChart';
import { SectionLabel } from '../kpi-dashboard/KpiDashboardContent';
import BandwidthHorizontalActivityCountBar from './BandwidthHorizontalActivityCountBar';
import BandwidthUserCountTimePanel from './BandwidthUserCountTimePanel';
import type { BandwidthDashboardData } from './bandwidthDashboardTypes';
import type { UsersChartDrill } from './bandwidthUsersDrill';

export interface BandwidthUsersGridProps {
  dashboardQuery: UseQueryResult<BandwidthDashboardData, Error>;
  usersChartDrill: UsersChartDrill;
  onUsersChartDrillChange: React.Dispatch<React.SetStateAction<UsersChartDrill>>;
  exportDateFrom: string;
  exportDateTo: string;
}

const BandwidthUsersGrid: React.FC<BandwidthUsersGridProps> = ({
  dashboardQuery,
  usersChartDrill,
  onUsersChartDrillChange,
  exportDateFrom,
  exportDateTo,
}) => {
  const { data, isLoading, isFetching, isError, error, refetch } = dashboardQuery;
  const baseError = isError ? error : null;

  const typeRows = useMemo(() => {
    const src = data?.activityTypeCountVsTime ?? [];
    return [...src]
      .sort((a, b) => b.totalActivityCount - a.totalActivityCount)
      .map((r) => ({
        label: r.activityTypeLabel,
        count: r.totalActivityCount,
        drillValue: r.activityType,
      }));
  }, [data?.activityTypeCountVsTime]);

  const customerRows = useMemo(() => {
    const src = data?.countsByCustomer ?? [];
    return [...src]
      .sort((a, b) => b.totalActivityCount - a.totalActivityCount)
      .map((r) => ({
        label: r.customer,
        count: r.totalActivityCount,
        drillValue: r.customerId,
      }));
  }, [data?.countsByCustomer]);

  const selectedWeekStart =
    usersChartDrill?.kind === 'week' ? usersChartDrill.weekStart : null;
  const selectedActivityType =
    usersChartDrill?.kind === 'activityType' ? usersChartDrill.activityType : null;
  const selectedCustomerId =
    usersChartDrill?.kind === 'customer' ? usersChartDrill.customerId : null;

  const onWeekBarClick = useCallback(
    (weekStart: string) => {
      onUsersChartDrillChange((prev) =>
        prev?.kind === 'week' && prev.weekStart === weekStart ? null : { kind: 'week', weekStart }
      );
    },
    [onUsersChartDrillChange]
  );

  const onActivityTypeRowClick = useCallback(
    (activityType: string) => {
      onUsersChartDrillChange((prev) =>
        prev?.kind === 'activityType' && prev.activityType === activityType
          ? null
          : { kind: 'activityType', activityType }
      );
    },
    [onUsersChartDrillChange]
  );

  const onCustomerRowClick = useCallback(
    (customerId: string) => {
      onUsersChartDrillChange((prev) =>
        prev?.kind === 'customer' && prev.customerId === customerId
          ? null
          : { kind: 'customer', customerId }
      );
    },
    [onUsersChartDrillChange]
  );

  if (isLoading && !data) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-2xl border border-indigo-100/80 bg-white/90 p-12 text-sm text-slate-500 shadow-sm shadow-indigo-900/5 ring-1 ring-slate-950/[0.03]">
        Loading user analytics…
      </div>
    );
  }

  if (baseError) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-2xl border border-red-200/90 bg-red-50/50 p-12 text-center shadow-sm">
        <p className="text-sm text-red-800">{baseError instanceof Error ? baseError.message : 'Could not load data.'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-red-800 shadow ring-1 ring-red-200 transition hover:bg-red-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const d = data!;
  const busyHint = isFetching ? 'Updating…' : undefined;

  return (
    <div className="flex flex-col">
      {busyHint ? (
        <p className="mb-4 text-xs font-medium text-slate-500" aria-live="polite">
          {busyHint}
        </p>
      ) : null}

      <section className="mb-6 w-full" aria-labelledby="users-activity-kpis-heading">
        <SectionLabel id="users-activity-kpis-heading">User activity KPIs</SectionLabel>
        <div className="w-full">
          <BandwidthActivityByWeekChart
            title="Activity By Week"
            points={d.activityByWeek}
            selectedWeekStart={selectedWeekStart}
            onWeekBarClick={onWeekBarClick}
          />
        </div>
      </section>

      <section className="mb-6 w-full">
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 lg:items-stretch">
          <div className="flex h-full min-h-0 flex-col">
            <BandwidthHorizontalActivityCountBar
              title="Activity By Type"
              rows={typeRows}
              selectedDrillValue={selectedActivityType}
              onDrillRowClick={onActivityTypeRowClick}
              exportActions={{
                filePrefix: 'bandwidth_users_activity_by_type',
                rowHeader: 'Activity type',
                valueHeader: 'Activity count',
                dateFrom: exportDateFrom,
                dateTo: exportDateTo,
              }}
            />
          </div>
          <div className="flex h-full min-h-0 flex-col">
            <BandwidthHorizontalActivityCountBar
              title="Activity by Customer"
              rows={customerRows}
              selectedDrillValue={selectedCustomerId}
              onDrillRowClick={onCustomerRowClick}
              exportActions={{
                filePrefix: 'bandwidth_users_activity_by_customer',
                rowHeader: 'Customer',
                valueHeader: 'Activity count',
                dateFrom: exportDateFrom,
                dateTo: exportDateTo,
              }}
            />
          </div>
        </div>
      </section>

      <section className="mb-6 w-full">
        <BandwidthUserCountTimePanel
          title="Activity count and time by user"
          rows={d.userCountVsTime}
          exportDateFrom={exportDateFrom}
          exportDateTo={exportDateTo}
        />
      </section>
    </div>
  );
};

export default BandwidthUsersGrid;
